import type { PlatformOSType } from 'react-native'

import type { ImageStyle } from 'expo-image'

const BORDER_RADIUS_STYLE_PROPERTIES = [
  'borderRadius',
  'borderTopLeftRadius',
  'borderTopRightRadius',
  'borderBottomLeftRadius',
  'borderBottomRightRadius',
  'borderTopStartRadius',
  'borderTopEndRadius',
  'borderBottomStartRadius',
  'borderBottomEndRadius',
  'borderStartStartRadius',
  'borderStartEndRadius',
  'borderEndStartRadius',
  'borderEndEndRadius',
] as const

const CSS_PIXEL_VALUE = /^(-?\d+(?:\.\d+)?)px$/
const CSS_PERCENTAGE_VALUE = /^\d+(?:\.\d+)?%$/

// Android clamps radii larger than the image bounds, preserving circular and pill-shaped images.
const FULLY_ROUNDED_NATIVE_RADIUS = 999_999

/**
 * Expo Image 3 exposes its Android border-radius props as doubles and crashes when
 * shared web/native styles pass CSS strings. React Native Views still support those
 * strings, so only normalize the style sent to Expo Image on native platforms.
 */
export const normalizeExpoImageStyle = (style: ImageStyle, platform: PlatformOSType): ImageStyle => {
  if (platform === 'web') {
    return style
  }

  const normalizedStyle = { ...style } as ImageStyle & Record<string, unknown>

  BORDER_RADIUS_STYLE_PROPERTIES.forEach((property) => {
    const value = normalizedStyle[property]
    if (typeof value !== 'string') {
      return
    }

    const pixelMatch = value.match(CSS_PIXEL_VALUE)
    if (pixelMatch) {
      normalizedStyle[property] = Number(pixelMatch[1])
      return
    }

    if (CSS_PERCENTAGE_VALUE.test(value)) {
      normalizedStyle[property] = FULLY_ROUNDED_NATIVE_RADIUS
      return
    }

    delete normalizedStyle[property]
  })

  return normalizedStyle
}
